import { useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { tokens } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { CART_PAGE } from '@fhs-legacy/frontend/src/utils/test-ids';

import AddMoreItemsButton from '../components/cart-components/add-more-items-button';
import { CartChallengeInlineAlert } from '../components/cart-components/challenge-inline-alert';
import ContinueButton from '../components/cart-components/continue-button';
import { CartDonationContainer } from '../components/cart-components/donations';
import { CartEmptyState } from '../components/cart-components/empty-state';
import CartItemsList from '../components/cart-components/item-list';
import { CartOfferItem } from '../components/cart-components/offer-item';
import { CartOffersAndPromosSections } from '../components/cart-components/offers-and-promos-section';
import { CartSubtotals } from '../components/cart-components/subtotal';
import SavingsWithOffer from '../components/savings-with-offer';
import { usePreloadPaymentMethods } from '../payment/hooks';
import { useCartValues } from '../state/cart-store';
import { calculateBaseTotal } from '../state/cart-store/pricing-cart/compute-cart-total';

export function CartReview() {
  usePreloadPaymentMethods();
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const { cartEntries } = useCartValues();
  const { calculatedCartTotal } = useOrderContext();
  const { rewardTierApplied } = useRewardTierApplied();

  const hasAnyRewardApplied = !!rewardTierApplied;

  const baseTotal = useMemo(() => calculateBaseTotal(cartEntries), [cartEntries]);
  const savings = useMemo(() => baseTotal - calculatedCartTotal, [baseTotal, calculatedCartTotal]);
  const donationAmount = useMemo(
    () => cartEntries.find(entry => entry.isDonation)?.price || 0,
    [cartEntries]
  );
  const scrollViewRef = useRef<ScrollView>(null!);
  const [isBadContinueAttempt, setIsBadContinueAttempt] = useState(false);

  if (cartEntries.filter(entry => !entry.isDonation).length === 0 && calculatedCartTotal === 0) {
    return <CartEmptyState />;
  }

  return (
    <View style={styles.page} testID={CART_PAGE}>
      <ScrollView
        ref={scrollViewRef}
        accessibilityRole="scrollbar"
        accessibilityLabel="Cart contents"
        showsVerticalScrollIndicator={false}
      >
        <CartDonationContainer
          isBadContinueAttempt={isBadContinueAttempt}
          setIsBadContinueAttempt={setIsBadContinueAttempt}
        />

        <CartItemsList />
        <CartChallengeInlineAlert />
        <AddMoreItemsButton />

        {selectedOffer ? <CartOfferItem /> : <CartOffersAndPromosSections />}

        <CartSubtotals
          baseTotal={baseTotal - donationAmount}
          grandTotal={calculatedCartTotal - donationAmount}
        />
      </ScrollView>

      <SafeAreaView>
        {(selectedOffer || hasAnyRewardApplied) && <SavingsWithOffer savings={savings} />}
        <ContinueButton
          scrollViewRef={scrollViewRef}
          setIsBadContinueAttempt={setIsBadContinueAttempt}
        />
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: 'white',
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: 1,
    flex: 1,
  },
  rewardErrorContainer: {
    marginHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
});
